import React, { useState, useEffect } from 'react';
import { 
  List, MapPin, Calendar, Layers, ExternalLink, 
  ChevronDown, ChevronUp, RefreshCw, CheckCircle, Clock, Download 
} from 'lucide-react';
import { baseurl } from '../../baseurl';
import axios from 'axios';
import * as XLSX from 'xlsx';

const BulkUpload = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedBatch, setExpandedBatch] = useState(null);
  
  const userToken = localStorage.getItem('userToken');

  useEffect(() => {
    if (isOpen) fetchBulkHistory();
  }, [isOpen]);

  const fetchBulkHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseurl}getBulkUploadsAll`, {
        headers: { Authorization: `Bearer ${userToken}` }
      });
      if (response.data.success) {
        setBatches(response.data.data);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (batches.length === 0) return;

    // Get the base URL of your current site for the project link
    const siteUrl = window.location.origin;

    const flatData = batches.flatMap(batch => 
      batch.projects.map(proj => ({
        "Batch Name": batch.bulk_title,
        "Upload Date": new Date(batch.created_at).toLocaleString(),
        "Property ID": proj.property_id,
        "Status": proj.status,
        "Address": proj.project_data?.address || "N/A",
        "Action Link": `${siteUrl}/project-details/${proj.id}` // Full URL for Excel
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(flatData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bulk Upload Report");

    // Setting column widths for better readability in Excel
    const wscols = [
        {wch: 25}, {wch: 20}, {wch: 15}, {wch: 12}, {wch: 40}, {wch: 50}
    ];
    worksheet['!cols'] = wscols;

    XLSX.writeFile(workbook, `Bulk_History_Report_${new Date().getTime()}.xlsx`);
  };

  const toggleModal = () => {
    setIsOpen(!isOpen);
    setExpandedBatch(null);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <>
      <button className="nav-tab active" onClick={toggleModal}>
        <List size={16} className="me-2" /> All Bulk Uploads
      </button>

      {isOpen && (
        <div className="bu-modal-overlay">
          <div className="bu-modal-container-large shadow-2xl">
            {/* MODAL HEADER */}
            <div className="bu-modal-header border-bottom bg-white sticky-top d-flex justify-content-between align-items-center p-3">
              <div className="d-flex align-items-center">
                <div className="bu-icon-circle me-3"><Layers size={22} color="#6d28d9" /></div>
                <div>
                  <h2 className="m-0 fs-5 fw-bold">Bulk Upload History</h2>
                  <p className="text-muted small m-0">Review batch processing results</p>
                </div>
              </div>
              
              <div className="d-flex gap-2">
                <button className="btn btn-success btn-sm rounded-pill d-flex align-items-center px-3" onClick={exportToExcel} disabled={batches.length === 0}>
                  <Download size={14} className="me-1" /> Export  
                </button>
                <button className="btn btn-light border btn-sm rounded-pill d-flex align-items-center" onClick={fetchBulkHistory} disabled={loading}>
                  <RefreshCw size={14} className={`me-1 ${loading ? 'spin' : ''}`} /> Refresh
                </button>
                <button className="bu-close-x" onClick={toggleModal}>&times;</button>
              </div>
            </div>

            {/* MODAL BODY */}
            <div className="bu-modal-body p-0" style={{ overflowY: 'auto', flex: 1 }}>
              <div className="table-responsive">
                <table className="table align-middle mb-0 main-batch-table">
                  <thead className="table-light sticky-top" style={{ zIndex: 10 }}>
                    <tr>
                      <th className="ps-4">Batch Title</th>
                      <th className="text-center">Count</th>
                      <th>Timestamp</th>
                      <th className="text-end pe-4">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && batches.length === 0 ? (
                      <tr><td colSpan="4" className="text-center py-5">
                        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                        Loading history...
                      </td></tr>
                    ) : batches.length > 0 ? (
                      batches.map((batch) => (
                        <React.Fragment key={batch.id}>
                          <tr 
                            className={`batch-row ${expandedBatch === batch.id ? 'bg-selected' : ''}`}
                            onClick={() => setExpandedBatch(expandedBatch === batch.id ? null : batch.id)}
                          >
                            <td className="ps-4">
                              <div className="d-flex align-items-center">
                                {expandedBatch === batch.id ? <ChevronUp size={16} className="text-primary me-2"/> : <ChevronDown size={16} className="text-muted me-2"/>}
                                <span className="fw-semibold text-dark">{batch.bulk_title}</span>
                              </div>
                            </td>
                            <td className="text-center">
                              <span className="badge rounded-pill bg-light text-primary border px-3">{batch.total_projects} Projects</span>
                            </td>
                            <td className="text-muted small">{formatDate(batch.created_at)}</td>
                            <td className="text-end pe-4">
                              <button className={`btn btn-sm rounded-pill px-3 ${expandedBatch === batch.id ? 'btn-primary' : 'btn-outline-primary'}`}>
                                {expandedBatch === batch.id ? 'Hide' : 'View Projects'}
                              </button>
                            </td>
                          </tr>

                          {/* NESTED PROJECTS TABLE */}
                          {expandedBatch === batch.id && (
                            <tr>
                              <td colSpan="4" className="bg-light p-0">
                                <div className="px-5 py-3">
                                  <div className="table-container shadow-sm rounded border overflow-hidden">
                                    <table className="table table-sm bg-white mb-0 nested-project-table">
                                      <thead className="bg-dark text-white">
                                        <tr>
                                          <th className="ps-3 py-2">Property ID</th>
                                          <th className="py-2">Address</th>
                                          <th className="text-center py-2">Status</th>
                                          <th className="text-center py-2">Action</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {batch.projects && batch.projects.length > 0 ? (
                                          batch.projects.map((proj) => (
                                            <tr key={proj.id}>
                                              <td className="ps-3 fw-bold text-primary" style={{ fontSize: '12px' }}>{proj.property_id}</td>
                                              <td className="small text-muted text-truncate" style={{ maxWidth: '350px' }}>
                                                <MapPin size={12} className="me-1" />
                                                {proj.project_data?.address || 'No address found'}
                                              </td>
                                              <td className="text-center">
                                                <span className={`status-tag ${proj.status.toLowerCase()}`}>
                                                  {proj.status === 'Completed' ? <CheckCircle size={10} className="me-1"/> : <Clock size={10} className="me-1"/>}
                                                  {proj.status}
                                                </span>
                                              </td>
                                              <td className="text-center">
                                                <a href={`/report/${proj.property_id}`} target="_blank" rel="noreferrer" className="btn-link-icon" title="View Project">
                                                  <ExternalLink size={14} />
                                                </a>
                                              </td>
                                            </tr>
                                          ))
                                        ) : (
                                          <tr><td colSpan="4" className="text-center py-3 text-muted">No projects found in this batch.</td></tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr><td colSpan="4" className="text-center py-5 text-muted">No bulk upload history available.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="bu-modal-footer bg-white border-top p-3 d-flex justify-content-end">
                <button className="btn btn-secondary px-4 rounded-pill" onClick={toggleModal}>Close History</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .bu-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(15, 23, 42, 0.65); display: flex; justify-content: center; align-items: center; z-index: 1050; backdrop-filter: blur(4px); }
        .bu-modal-container-large { background: #fff; width: 1100px; max-width: 95vw; height: 85vh; border-radius: 16px; display: flex; flex-direction: column; overflow: hidden; }
        .bu-icon-circle { width: 40px; height: 40px; background: #f5f3ff; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
        .bu-close-x { background: none; border: none; font-size: 32px; line-height: 1; color: #94a3b8; cursor: pointer; padding: 0 10px; transition: color 0.2s; }
        .bu-close-x:hover { color: #ef4444; }
        
        /* Main Table Style */
        .main-batch-table thead th { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; padding: 16px; background: #f8fafc; border-bottom: 2px solid #e2e8f0; }
        .batch-row { cursor: pointer; transition: background 0.2s; }
        .batch-row:hover { background: #f1f5f9; }
        .bg-selected { background: #f8fafc !important; border-left: 4px solid #6d28d9; }

        /* Nested Project Table */
        .nested-project-table { font-size: 13px; }
        .nested-project-table thead th { font-weight: 500; border: none; letter-spacing: 0.02em; }
        .nested-project-table tbody tr { border-bottom: 1px solid #f1f5f9; }
        .nested-project-table tbody tr:last-child { border-bottom: none; }
        
        .status-tag { display: inline-flex; align-items: center; padding: 2px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; text-transform: capitalize; }
        .status-tag.completed { background: #dcfce7; color: #166534; }
        .status-tag.pending { background: #fef9c3; color: #854d0e; }

        .btn-link-icon { color: #6d28d9; padding: 5px; display: inline-block; transition: transform 0.2s; }
        .btn-link-icon:hover { transform: scale(1.2); color: #4c1d95; }

        .spin { animation: rotation 1s infinite linear; }
        @keyframes rotation { from { transform: rotate(0deg); } to { transform: rotate(359deg); } }
      `}</style>
    </>
  );
};

export default BulkUpload;